import React, { useState, useEffect } from 'react';
import { 
  Plus, Pencil, Trash2, Search, Loader2, BookOpen 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from '@/components/ui/use-toast';
import AddressWithMap from '@/components/AddressWithMap';

const INITIAL_CARTORIO = {
  id: '',
  nome: '',
  cep: '',
  endereco: '', // Full address string or structured
  cidade: '',
  uf: '',
  telefone: '',
  googleMapsLink: ''
};

const CartorioManager = () => {
  const [cartorios, setCartorios] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState(INITIAL_CARTORIO);
  const [isLoadingCep, setIsLoadingCep] = useState(false);

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem('cartorios') || '[]');
    setCartorios(stored);
  }, []);

  const handleSave = (e) => {
    e.preventDefault();
    if (!formData.nome) {
        toast({ title: "Nome obrigatório", variant: "destructive" });
        return;
    }

    let newList;
    if (formData.id) {
        newList = cartorios.map(c => c.id === formData.id ? formData : c);
        toast({ title: "Cartório atualizado" });
    } else {
        newList = [...cartorios, { ...formData, id: crypto.randomUUID() }];
        toast({ title: "Cartório cadastrado" });
    }
    
    setCartorios(newList);
    localStorage.setItem('cartorios', JSON.stringify(newList));
    setIsModalOpen(false);
  };

  const handleDelete = (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este cartório?')) return;
    const newList = cartorios.filter(c => c.id !== id);
    setCartorios(newList);
    localStorage.setItem('cartorios', JSON.stringify(newList));
    toast({ title: "Cartório removido" });
  };

  const openModal = (cartorio = null) => {
    setFormData(cartorio || INITIAL_CARTORIO);
    setIsModalOpen(true);
  };

  const handleCepBlur = async () => {
    const cleanCep = formData.cep.replace(/\D/g, '');
    if (cleanCep.length !== 8) return;

    setIsLoadingCep(true);
    try {
        const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
        const data = await response.json();
        if (!data.erro) {
             setFormData(prev => ({
                 ...prev,
                 endereco: `${data.logradouro}, ${data.bairro}`,
                 cidade: data.localidade,
                 uf: data.uf
             }));
             toast({ title: "Endereço encontrado!" });
        }
    } catch (error) {
        // Silent fail or toast
    } finally {
        setIsLoadingCep(false);
    }
  };

  const filtered = cartorios.filter(c => 
    c.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.cidade.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="container mx-auto px-4 max-w-5xl py-8 animate-in fade-in">
      <div className="flex justify-between items-center mb-6">
        <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
                <BookOpen className="w-8 h-8 text-indigo-600" />
                Cartórios de Registro (CRI)
            </h1>
            <p className="text-slate-500">Gerencie os cartórios para vínculo com imóveis.</p>
        </div>
        <Button onClick={() => openModal()} className="bg-indigo-600">
            <Plus className="w-4 h-4 mr-2" /> Novo Cartório
        </Button>
      </div>

      <Card className="mb-6">
        <CardContent className="p-4">
            <div className="relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                <Input 
                    placeholder="Pesquisar por nome ou cidade..." 
                    className="pl-10"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                />
            </div>
        </CardContent>
      </Card>

      <div className="bg-white rounded-md border shadow-sm overflow-hidden">
        <Table>
            <TableHeader className="bg-slate-50">
                <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Localização</TableHead>
                    <TableHead>Contato</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {filtered.length === 0 ? (
                    <TableRow>
                        <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                            Nenhum cartório cadastrado.
                        </TableCell>
                    </TableRow>
                ) : (
                    filtered.map(c => (
                        <TableRow key={c.id}>
                            <TableCell className="font-medium">{c.nome}</TableCell>
                            <TableCell>
                                <AddressWithMap text={c.endereco} data={c} />
                                <div className="text-xs text-slate-500 pl-5 mt-0.5">{c.cidade}/{c.uf} - CEP: {c.cep}</div>
                            </TableCell>
                            <TableCell>
                                <div className="text-sm">{c.telefone}</div>
                            </TableCell>
                            <TableCell className="text-right">
                                <Button variant="ghost" size="icon" onClick={() => openModal(c)}>
                                    <Pencil className="w-4 h-4 text-slate-500" />
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => handleDelete(c.id)}>
                                    <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                            </TableCell>
                        </TableRow>
                    ))
                )}
            </TableBody>
        </Table>
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent>
            <DialogHeader>
                <DialogTitle>{formData.id ? 'Editar Cartório' : 'Novo Cartório'}</DialogTitle>
                <DialogDescription>Dados de contato e localização.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSave} className="space-y-4">
                <div className="space-y-2">
                    <Label>Nome do Cartório *</Label>
                    <Input value={formData.nome} onChange={e => setFormData({...formData, nome: e.target.value})} required placeholder="Ex: 1º Cartório de Registro de Imóveis" />
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2 col-span-1">
                        <Label>CEP</Label>
                        <div className="relative">
                            <Input 
                                value={formData.cep} 
                                onChange={e => setFormData({...formData, cep: e.target.value.replace(/\D/g,'').slice(0,8)})} 
                                onBlur={handleCepBlur}
                                placeholder="00000-000"
                            />
                            {isLoadingCep && <Loader2 className="absolute right-2 top-2.5 w-4 h-4 animate-spin text-indigo-600" />}
                        </div>
                    </div>
                     <div className="space-y-2 col-span-2">
                        <Label>Cidade</Label>
                        <Input value={formData.cidade} onChange={e => setFormData({...formData, cidade: e.target.value})} />
                    </div>
                </div>

                <div className="space-y-2">
                    <Label>Endereço Completo</Label>
                    <Input value={formData.endereco} onChange={e => setFormData({...formData, endereco: e.target.value})} placeholder="Rua, Número, Bairro" />
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label>Telefone</Label>
                        <Input value={formData.telefone} onChange={e => setFormData({...formData, telefone: e.target.value})} />
                    </div>
                     <div className="space-y-2">
                        <Label>Link Google Maps (Opcional)</Label>
                        <Input value={formData.googleMapsLink} onChange={e => setFormData({...formData, googleMapsLink: e.target.value})} placeholder="URL manual se necessário" />
                    </div>
                </div>

                <DialogFooter>
                    <Button type="submit">Salvar Cartório</Button>
                </DialogFooter>
            </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CartorioManager;